'use client';

import React, { type FC, useMemo } from 'react';
import type { PriceBoxProps } from '../../price.types';
import { cn } from '../../../../../../utils/cn';
import { Text } from '../../../../../../atoms/text';
import { getBasePrice, getFinalPrice, getMinimalPrice, usePrice } from '../../price.helper';
import { DiscountConfigurable } from '../discount-configurable';

import styles from '../../final-price.module.css';
import { PriceSizeEnum } from '../../price.enum';

export const FinalPriceConfigurable: FC<PriceBoxProps> = ({
    product,
    variant,
    variation,
    variantSize = PriceSizeEnum.SMALL,
}) => {
    const selectedProduct = variation ? variation.product : product;

    const finalPrice = getFinalPrice(selectedProduct);
    const productBasePrice = getBasePrice(selectedProduct);
    const minimumPrice = getMinimalPrice(selectedProduct);

    const { basePrice, discount } = usePrice({
        amount: finalPrice.value ?? 0,
        baseAmount: productBasePrice.value ?? 0,
        currencyCode: finalPrice.currency ?? 'VND',
    });

    const { price: minPrice } = usePrice({
        amount: minimumPrice.value!,
        currencyCode: minimumPrice.currency ?? 'VND',
    });

    const layoutSize = useMemo(() => {
        switch (variant) {
            case 'horizontal':
                return styles.horizontal;
            case 'details':
                return styles.details;
            default:
                return styles.mediumText;
        }
    }, [variant]);

    const sizeClass = useMemo(() => (variantSize === PriceSizeEnum.BIG ? styles.big : styles.small), [variantSize]);
    const priceClasses = useMemo(() => cn(styles.root, layoutSize), [layoutSize]);
    const containerClass = useMemo(
        () =>
            cn(
                variant === 'horizontal' ? styles.blockHorizontal : styles.blockVertical,
                variant === 'details' && styles.fit,
                sizeClass
            ),
        [variant, sizeClass]
    );

    return (
        <div className={containerClass}>
            <div className={styles.priceWrapper}>
                <Text className={priceClasses}>{minPrice}</Text>

                {discount && basePrice && (
                    <DiscountConfigurable
                        basePrice={basePrice}
                        price={minPrice}
                        variant={variant}
                        product={product}
                        variantSize={variantSize}
                    />
                )}
            </div>
        </div>
    );
};
